import { HttpProgressEvent, HttpResponse } from "@angular/common/http";
import { CallAgent } from "@azure/communication-calling";
import { ChatThreadClient } from "@azure/communication-chat";
import { StatefulCallClient, StatefulChatClient } from "@azure/communication-react";
import { guid, LiveSessionState } from "@pcp/core/interfaces";
import { Observable } from "rxjs";
import { StateCreator } from "zustand/index";
import { PcpLiveLessonState } from "../pcpLiveLessonStore.interface";
import { isInteractiveWidgetSupported, useIsMobile, useVisualViewport } from "../../utils";
import { useOrientation, useWindowSize } from "@uidotdev/usehooks";
import { useEffect, useMemo } from "react";
import { PcpLiveLessonStore } from "../pcpLiveLessonStore";

export type CreatePcpSharedContextProps = {
    callClient?: StatefulCallClient;
    callAgent?: CallAgent;
    chatClient?: StatefulChatClient;
    chatThreadClient?: ChatThreadClient;
    acsToken: string;
    activeElement: HTMLElement | null;
    pcpCallState$: Observable<LiveSessionState>;
    lessonId: guid;
    pcpToken: string;

    apiGet: <TResponse, TRequest>(url: string, data?: TRequest) => Promise<TResponse>;
    apiPost: <TResponse, TRequest>(url: string, data: TRequest) => Promise<TResponse>;
    apiPostProgress: <TResponse, TRequest>(
        url: string,
        data: TRequest,
        handlers: {
            onUploadProgress: (event: HttpProgressEvent) =>void,
        }
    ) => Promise<HttpResponse<TResponse>>;
    apiDelete: <TResponse, TRequest>(url: string, data?: TRequest) => Promise<TResponse>;
}

export type PcpSharedContextType = CreatePcpSharedContextProps & {
    isMobile: boolean;
    screenOrientation: "portrait" | "landscape";
    windowSize: { width: null|number, height: null|number };
    viewportSize: { height: null|number };
    isDesktopWithSmallHeight: boolean;
    // unwrapped observable
    pcpCallState: LiveSessionState;
    isActiveElementFullScreen: boolean;

    setIsMobile: (isMobile: boolean) => void;
    setScreenOrientation: (orientation: "portrait" | "landscape") => void;
    setWindowSize: (size: { width: null|number, height: null|number }) => void;
    setViewportSize: (size: { height: null|number }) => void;
    setIsDesktopWithSmallHeight: (isDesktopWithSmallHeight: boolean) => void;
    setPcpCallState: (state: LiveSessionState) => void;
    setIsActiveElementFullScreen: (isFullScreen: boolean) => void;
    clearTokens: () => void;
};

export const createPcpSharedSlice = (props: CreatePcpSharedContextProps): StateCreator<
    PcpLiveLessonState,
    [["zustand/immer", never]],
    [],
    PcpSharedContextType
> => (set, get) => ({
    ...props,

    isMobile: false,
    screenOrientation: 'landscape',
    isDesktopWithSmallHeight: false,
    windowSize: { width: null, height: null },
    viewportSize: { height: null },
    pcpCallState: 'waiting',
    isActiveElementFullScreen: false,

    setIsDesktopWithSmallHeight: (isDesktopWithSmallHeight: boolean) => {
        set(state => {
            state.isDesktopWithSmallHeight = isDesktopWithSmallHeight;
        });
    },

    setIsMobile: (isMobile: boolean) => {
        set(state => {
            state.isMobile = isMobile;
        });
    },

    setScreenOrientation: (orientation: "portrait" | "landscape") => {
        set(state => {
            state.screenOrientation = orientation;
        });
    },

    setWindowSize: (size: { width: null|number, height: null|number }) => {
        set(state => {
            state.windowSize = size;
        });
    },

    setViewportSize: (size: { height: null|number }) => {
        set(state => {
            state.viewportSize = size;
        });
    },

    setPcpCallState: (state: LiveSessionState) => {
        set(currentState => {
            currentState.pcpCallState = state;
        });
    },

    setIsActiveElementFullScreen: (isFullScreen: boolean) => {
        set(state => {
            state.isActiveElementFullScreen = isFullScreen;
        });
    },

    clearTokens: () => {
        localStorage.clear();
        sessionStorage.clear();
    },

});


export const useSyncSharedSlice = (store: PcpLiveLessonStore) => {

    const setScreenOrientation = store.getState().setScreenOrientation;
    const setIsMobile = store.getState().setIsMobile;
    const setIsActiveElementFullScreen = store.getState().setIsActiveElementFullScreen;
    const setWindowSize = store.getState().setWindowSize;
    const setViewportSize = store.getState().setViewportSize;
    const setIsDesktopWithSmallHeight = store.getState().setIsDesktopWithSmallHeight;
    const setPcpCallState = store.getState().setPcpCallState;

    const pcpCallState$ = store.getState().pcpCallState$;
    const isActiveElementFullScreen = store.getState().isActiveElementFullScreen;
    const orientation = useOrientation();
    const orientationString = useMemo(
        () => orientation.type.startsWith("landscape") ? "landscape" : "portrait",
        [orientation.type]
    );

    const windowSize = useWindowSize();
    const isMobile = useIsMobile();
    const viewportSize = useVisualViewport(isMobile);

    const isDesktopWithSmallHeight = useMemo(
        () => !isMobile && windowSize.height !== null && windowSize.height < 900,
        [isMobile, windowSize]
    );


    useEffect(() => {
        const subscription = pcpCallState$.subscribe(setPcpCallState);
        return () => subscription.unsubscribe();
    }, [pcpCallState$, setPcpCallState]);

    useEffect(() => setScreenOrientation(orientationString), [orientationString, setScreenOrientation]);
    useEffect(() => setIsMobile(isMobile), [isMobile, setIsMobile]);
    useEffect(() => setIsActiveElementFullScreen(isActiveElementFullScreen), [isActiveElementFullScreen, setIsActiveElementFullScreen]);
    useEffect(() => setWindowSize(windowSize), [windowSize, setWindowSize]);
    useEffect(() => setViewportSize(viewportSize), [viewportSize, setViewportSize]);
    useEffect(() => setIsDesktopWithSmallHeight(isDesktopWithSmallHeight), [isDesktopWithSmallHeight, setIsDesktopWithSmallHeight]);
}
